import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import PageCard from "components/admin/PageCard";
import { useConfirm } from "components/admin/Feedback";
import { diffLines } from "components/admin/confirmChanges";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";

function fmt(value, isInt) {
  if (value === null || value === undefined) return "";
  return isInt ? String(Math.round(value)) : String(Number(value.toFixed(4)));
}

/** Một bảng theo cấp: mỗi ô một ô nhập, lưu cả bảng một lần. */
function ArrayCard({ item, onSaved }) {
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const [draft, setDraft] = useState([]);
  const canEdit = useCan(PERMISSIONS.BALANCE_EDIT);

  useEffect(() => {
    setDraft(item.values.map((v) => fmt(v, item.isInt)));
  }, [item]);

  const bad = useMemo(
    () =>
      draft.some((d) => {
        const n = Number(d);
        return d === "" || Number.isNaN(n) || n < item.min || n > item.max;
      }),
    [draft, item]
  );

  const dirty = useMemo(
    () =>
      draft.some(
        (d, i) => !Number.isNaN(Number(d)) && Math.abs(Number(d) - item.values[i]) > 0.0001
      ),
    [draft, item]
  );

  const save = async () => {
    const cell = (i) => item.itemLabels[i] || `ô ${i + 1}`;
    const { lines, jumps } = diffLines(
      Object.fromEntries(item.values.map((v, i) => [i, v])),
      Object.fromEntries(draft.map((v, i) => [i, Number(v)])),
      item.values.map((v, i) => [i, cell(i)])
    );
    const ok = await confirm({
      title: `Lưu ${item.label}`,
      message: [
        ...lines,
        "",
        jumps.length
          ? `⚠ ${jumps.join(", ")} thay đổi từ 2 lần trở lên — kiểm tra lại có gõ nhầm không.`
          : null,
        "Áp dụng ngay cho trận đánh mới, không cần khởi động lại máy chủ.",
      ]
        .filter((l) => l !== null)
        .join("\n"),
      color: jumps.length ? "error" : "warning",
      confirmText: "Lưu",
    });
    if (!ok) return;
    const res = await run(
      () => api.put("/api/admin/balance/arrays", { key: item.key, values: draft.map(Number) }),
      (data) => `Đã đổi ${data.changed} ô`
    );
    if (res.ok) onSaved();
  };

  const reset = async () => {
    const ok = await confirm({
      title: "Khôi phục mặc định",
      message: `Đưa cả bảng ${item.label} về giá trị gốc trong code. Mọi ô đã sửa sẽ mất.`,
      confirmText: "Khôi phục",
    });
    if (!ok) return;
    const res = await run(
      () => api.post("/api/admin/balance/arrays/reset", { key: item.key }),
      "Đã khôi phục"
    );
    if (res.ok) onSaved();
  };

  return (
    <MDBox mb={3}>
      <MDBox display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
        <MDTypography variant="button" fontWeight="medium">
          {item.label}
          {item.unit ? ` (${item.unit})` : ""}
        </MDTypography>
        <Tooltip title={`Mã kỹ thuật: ${item.key}`}>
          <MDTypography variant="caption" color="text">
            Cho phép {item.min} – {item.max} · {item.values.length} ô
          </MDTypography>
        </Tooltip>
        <MDBox flexGrow={1} />
        {canEdit && (
          <>
            <MDButton size="small" color="info" disabled={busy || !dirty || bad} onClick={save}>
              Lưu…
            </MDButton>
            <MDButton
              size="small"
              color="secondary"
              variant="text"
              disabled={busy || !item.changed}
              onClick={reset}
            >
              <Icon>restart_alt</Icon>&nbsp;Mặc định
            </MDButton>
          </>
        )}
      </MDBox>
      {item.doc && (
        <MDTypography variant="caption" color="text" display="block" mb={1}>
          {item.doc}
        </MDTypography>
      )}
      <MDBox display="flex" flexWrap="wrap" gap={1}>
        {draft.map((value, i) => {
          const n = Number(value);
          const cellBad = value === "" || Number.isNaN(n) || n < item.min || n > item.max;
          const cellDirty = !cellBad && Math.abs(n - item.values[i]) > 0.0001;
          return (
            // Ô của bảng cố định theo chỉ số, không sắp xếp lại nên dùng index làm key là an toàn.
            // eslint-disable-next-line react/no-array-index-key
            <MDBox key={i} width={92}>
              <MDTypography variant="caption" color="text" display="block" noWrap>
                {item.itemLabels[i] || `ô ${i + 1}`}
              </MDTypography>
              <MDInput
                type="number"
                value={value}
                error={cellBad}
                success={cellDirty}
                inputProps={{ step: item.isInt ? 1 : "any", min: item.min, max: item.max }}
                sx={{ width: "100%" }}
                InputProps={{ readOnly: !canEdit }}
                onChange={(e) => {
                  const next = [...draft];
                  next[i] = e.target.value;
                  setDraft(next);
                }}
              />
            </MDBox>
          );
        })}
      </MDBox>
    </MDBox>
  );
}

ArrayCard.propTypes = {
  item: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
};

function ArrayTables() {
  const groups = useFetch(() => api.get("/api/admin/balance/arrays"), []);

  return (
    <MDBox>
      <Alert severity="info" sx={{ mb: 2 }}>
        Các bảng tỉ lệ và giá theo từng cấp. Số ô cố định theo code — sửa giá trị chứ không thêm bớt
        ô được. Lưu xong áp dụng ngay cho trận mới.
      </Alert>
      {groups.error && <Alert severity="error">{groups.error}</Alert>}
      {groups.loading && <MDTypography variant="button">Đang tải…</MDTypography>}
      <Grid container spacing={3}>
        {(groups.data || []).map((group) => (
          <Grid item xs={12} key={group.group}>
            <PageCard title={group.group} color="dark">
              <MDBox pt={2}>
                {group.items.map((item) => (
                  <ArrayCard key={item.key} item={item} onSaved={groups.reload} />
                ))}
              </MDBox>
            </PageCard>
          </Grid>
        ))}
      </Grid>
    </MDBox>
  );
}

export default ArrayTables;
